import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  MenuItem,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'

import {
  COLOR_LAST_COMPLETED_JOB,
  MEAN_DAILY_ENDPOINTS_INFO,
  MEAN_DAILY_PERIOD_INFO,
  MEAN_DAILY_VALUES_INFO,
  SUPPLIER_ACTIVITY_AGE_BANDS,
  SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT,
} from 'components/reports/Metrics/constants/SupplierActivityRecency'
import { DATE_FORMAT } from 'constants/DateFormats'
import { ActivityAgeBand, SupplierActivityMeanSeriesDto } from 'generated/graphql'

import { MeanDailyValuesChart } from './MeanDailyValuesChart'
import { MeanDailyValuesEndpoint } from './MeanDailyValuesEndpoint'

interface Props {
  band: ActivityAgeBand
  onBandChange: (band: ActivityAgeBand) => void
  data?: SupplierActivityMeanSeriesDto | null
  loading?: boolean
}

export const MeanDailyValuesCard = ({ band, onBandChange, data, loading }: Props) => (
  <Paper
    variant='outlined'
    sx={{ p: 2.5, borderRadius: 2, borderTop: `3px solid ${COLOR_LAST_COMPLETED_JOB}` }}>
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={2}
      justifyContent='space-between'
      alignItems={{ xs: 'flex-start', md: 'flex-start' }}
      mb={2}>
      <Box>
        <Stack direction='row' spacing={1} alignItems='center'>
          <Typography variant='subtitle1' fontWeight={700} sx={{ color: 'primary.main' }}>
            Mean Daily Values (per day)
          </Typography>
          <Tooltip title={MEAN_DAILY_VALUES_INFO}>
            <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
          </Tooltip>
        </Stack>
        <Typography variant='body2' color='primary.main'>
          Average number of days since last activity.
        </Typography>
        {data?.analysisDate && (
          <Typography variant='caption' color='text.secondary' display='block'>
            Last updated {format(new Date(data.analysisDate), DATE_FORMAT)}
          </Typography>
        )}
      </Box>

      <Stack direction='row' spacing={1} alignItems='center'>
        <TextField
          select
          size='small'
          label='View period (for each day)'
          value={band}
          onChange={(event) => onBandChange(event.target.value as ActivityAgeBand)}
          sx={{ minWidth: 200 }}>
          {SUPPLIER_ACTIVITY_AGE_BANDS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>
        <Tooltip title={MEAN_DAILY_PERIOD_INFO}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        </Tooltip>
      </Stack>

      <Stack direction='row' spacing={1} alignItems='center'>
        <Tooltip
          title={MEAN_DAILY_ENDPOINTS_INFO}
          slotProps={{ tooltip: { sx: { whiteSpace: 'pre-line', maxWidth: 420 } } }}>
          <InfoOutlinedIcon fontSize='small' sx={{ color: 'text.disabled' }} />
        </Tooltip>
        <Stack direction='row' spacing={3}>
          <MeanDailyValuesEndpoint
            caption={
              data?.today?.daysBeforeAnalysisDate
                ? `${data.today.daysBeforeAnalysisDate} days ago`
                : 'Today (0 days ago)'
            }
            bid={data?.today?.meanDaysSinceLastBid}
            job={data?.today?.meanDaysSinceLastCompletedJob}
            loading={loading}
          />
          <MeanDailyValuesEndpoint
            caption={`${data?.oldest?.daysBeforeAnalysisDate ?? 90} days ago`}
            bid={data?.oldest?.meanDaysSinceLastBid}
            job={data?.oldest?.meanDaysSinceLastCompletedJob}
            loading={loading}
          />
        </Stack>
      </Stack>
    </Stack>

    {loading ? (
      <Skeleton
        variant='rectangular'
        height={SUPPLIER_ACTIVITY_RECENCY_CHART_HEIGHT}
        sx={{ borderRadius: 1 }}
      />
    ) : (
      <MeanDailyValuesChart points={data?.points ?? []} />
    )}
  </Paper>
)
